// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react'
import { useTranslation } from "react-i18next";
import { firestore } from '../../../context/FirebaseConfig.js';
import { AuthContext } from '../../../context/AuthContext.js';
import { updateDoc, query, collection, doc, getDocs, where, arrayRemove, setDoc, serverTimestamp, deleteField } from 'firebase/firestore'

import "./ConflictsObservationModal.scss"
import ObservationGallery from '../../../components/FullscreenObservationModal/ObservationGallery.jsx';
import Select from 'react-select';
import QualitySliderDropdown from '../../../shared_components/QualitySliderDropdown/QualitySliderDropdown.jsx';

// Import Icons
import close_icon from "../../../assets/icons/close.svg"

export default function ConflictsObservationModal({ observation, closeFullscreenModal, getNext, getPrevious }) {
    const { i18n, t } = useTranslation();
    const { currentUser } = React.useContext(AuthContext);
    const { labelsets, logEventCustom } = React.useContext(AuthContext);
    const [ labelOptions, setLabelOptions ] = React.useState({});
    const [ selectedLabels, setSelectedLabels ] = React.useState({});
    const [ users, setUsers ] = React.useState({});

    async function getUsers() {
        /* Load all users in the company */
        const usersQuery = query(collection(firestore, "users"), where("company", "==", currentUser.company.company));
        const usersSnapshot = await getDocs(usersQuery);
        const users = {};
        usersSnapshot.forEach(doc => {
            users[doc.id] = doc.data();
        });
        setUsers(users);
    }

    React.useEffect(() => {
        getUsers();
    }, []);

    React.useEffect(() => {
        buildLabelOptions();
        buildSelectedLabels();
    }, [observation]);

    function buildLabelOptions() {
        /* Build the label options for the select dropdowns */
        const labelOptions = {};
        for (const labelsetId of observation?.labeling_conflict?.labelsets ?? []) {
            labelOptions[labelsetId] = getOptionsForLabelset(labelsetId);
        }
        setLabelOptions(labelOptions);
    }

    function getOptionsForLabelset(labelsetId) {
        /* Get the options for the select dropdown for the labelset */
        if (!labelsetId in labelsets) {
            return [];
        }

        const labelset = labelsets[labelsetId];
        if (labelset.type === "multiple-choice") {
            // TODO: Implement the multiple-choice labelset
            return [];
        } else if (labelset.type === "single-choice-with-slider") {
            return Object.entries(labelset.labels).map(([labelId, label]) => {
                return {
                    value: labelId,
                    label: label.full_name,
                    continuous_value: label.continuous_value,
                }
            });
        } else { // single-choice
            return Object.entries(labelset.labels).map(([labelId, label]) => {
                return {
                    value: labelId,
                    label: label.full_name,
                }
            });
        }
    }

    function buildSelectedLabels() {
        /* Build the selected labels for the select dropdowns */
        const selectedLabels = {};
        for (const labelsetId of observation?.labeling_conflict?.labelsets ?? []) {
            const labelsetType = labelsets?.[labelsetId]?.type ?? "single-choice";
            if (labelsetType === "multiple-choice") {
                // TODO: Implement the multiple-choice labelset
            } else {
                const resolution = observation?.labeling_conflict?.resolution?.[labelsetId] ?? {};
                const value = resolution?.label;
                const displayName = labelsets[labelsetId]?.labels?.[value]?.full_name;

                if (labelsetType === "single-choice-with-slider") {
                    const continuousValue = 
                        resolution?.continuous_value ?? // Use continuous value if it exists
                        labelsets[labelsetId]?.labels?.[value]?.continuous_value; // If not use default value associated with label
                    selectedLabels[labelsetId] = {
                        value: value,
                        label: displayName,
                        continuous_value: continuousValue,
                    };
                } else {
                    selectedLabels[labelsetId] = {
                        value: value,
                        label: displayName,
                    };
                }
            }
        }

        setSelectedLabels(selectedLabels);
    }

    function onLabelChange(labelsetId, selectedOption) {
        /* Write the selected label for the labeling conflict */
        let resolutionLabel = {};
        if (!selectedOption?.value) {
            resolutionLabel = deleteField();
        } else {
            resolutionLabel = {
                label: selectedOption.value,
                user: currentUser.uid,
                timestamp: serverTimestamp(),
            }
            
            if (labelsets[labelsetId].type === "single-choice-with-slider") {
                resolutionLabel.continuous_value = selectedOption.continuous_value;
            }
        }

        setDoc(doc(firestore, "clients", currentUser.company.company, "observations", observation.doc_id), {
            labeling_conflict: {
                resolution: {
                    [labelsetId]: resolutionLabel
                }
            }
        }, { merge: true });
    }
    
    return (
        <div className="conflicts-observation-modal">
            <div className="conflicts-modal--center">
                <ObservationGallery
                    observation={observation}
                    getNext={getNext}
                    getPrevious={getPrevious}
                />
            </div>
            <div className="conflicts-modal--right">
                <div className="conflicts-modal--buttons">
                    <div className="conflicts-modal--button" onClick={closeFullscreenModal}>
                        <img className="conflicts-modal--button-icon" src={close_icon}/>
                    </div>
                </div>
                <div className="conflicts-labeling-panel">
                    {Object.keys(labelOptions).map(labelsetId => {
                        return(
                            <div key={labelsetId}>
                                <p className="material-label-title">{labelsets[labelsetId].name}:</p>
                                {labelsets[labelsetId].type === "single-choice-with-slider" ?
                                // Return dropdown with slider for single-choice-with-slider labelsets
                                <QualitySliderDropdown 
                                    labelOptions={labelOptions[labelsetId]}
                                    selectedLabel={selectedLabels[labelsetId]}
                                    onLabelChange={(e) => {onLabelChange(labelsetId, e)}}
                                    showDropdown={labelsets[labelsetId].show_dropdown ?? true}
                                /> :
                                // Return normal dropdown for single-choice labelsets
                                <Select
                                    isClearable={true}
                                    isSearchable={false}
                                    options={labelOptions[labelsetId]}
                                    value={selectedLabels[labelsetId]}
                                    onChange={(e) => {onLabelChange(labelsetId, e)}}
                                    className="material-label-select"
                                    styles={{
                                        option: (base, state) => ({
                                            ...base,
                                            padding: '0.2rem 0rem 0.2rem 0.5rem',
                                            ':hover': {
                                                ...base[':hover'],
                                                backgroundColor: "#b5d4ff",
                                            },
                                        }),
                                    }}
                                />}

                                {Object.keys(observation?.labels?.[labelsetId] ?? {}).map(userId => {
                                    const labelId = observation?.labels[labelsetId][userId];
                                    if (!labelId) return;
                                    const userName = users[userId]?.displayName ?? userId;
                                    return <p key={userId} className="material-label--annotations">{labelsets[labelsetId].labels[labelId].display_name} - {userName}</p>
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}